import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';

import { WATERMARK_PLACEMENTS } from '../../../../../../constants/watermark-placements';
import { photoUploadFilter } from '../../../../../../util/photo-upload';
import { getErrorString } from '../../../../../../util/normalize-errors';
import ImageUpload from '../../../../../../components/global/ImageUpload';
import { IMAGE_SIZE_CODE } from '../../../../../../constants/image-size-codes';
import CustomControl from '../../../../../../components/global/CustomControl';
import { getAsset } from '../../../../../../util/assets';
import Alert from '../../../../../../components/global/Alert';
import { MeQuery } from '../../../../../../graphql/queries/account';
import { assetImagesUpload as assetImagesUploadMutation } from '../../../../../../graphql/mutations/account';
import { photoUploadSuccess as photoUploadSuccessAction } from '../../../../../../actions/photo-upload';
import useBasename from '../../../../../../components/routing/useBasename';
import { BASENAME } from '../../../../../../constants/basenames';

const propTypes = {
  userId: PropTypes.string,
  assetImagesUpload: PropTypes.func.isRequired,
  photoUploadSuccess: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
  values: PropTypes.shape({
    watermark_config: PropTypes.shape({
      id: PropTypes.string,
      asset_image: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }),
      placement: PropTypes.oneOf(Object.values(WATERMARK_PLACEMENTS)),
    }),
  }),
  disabled: PropTypes.bool,
};

const AlbumFolderBranding = ({
  userId,
  assetImagesUpload,
  photoUploadSuccess,
  handleChange,
  values,
  disabled = false,
}) => {
  const basename = useBasename();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onBrandFilterChange = async event => {
    const [image] = event.target.files;

    if (!image) {
      return;
    }

    if (!photoUploadFilter(image)) {
      setError('JPG & PNG only');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const assetImagesUploadResponse = await assetImagesUpload(userId, [
        { filename: image.name, content_type: image.type },
      ]);
      const [
        assetImageUploadData,
      ] = assetImagesUploadResponse.data.assetImagesUpload.image_uploads;

      await axios.put(assetImageUploadData.url, image, {
        headers: { 'Content-Type': image.type },
      });

      photoUploadSuccess({ id: assetImageUploadData.id, file: image }, true);

      handleChange({
        target: {
          name: 'watermark_config',
          value: {
            ...values.watermark_config,
            asset_image: {
              id: assetImageUploadData.id,
            },
          },
        },
      });
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onBrandFilterDismiss = () => {
    handleChange({
      target: {
        name: 'watermark_config',
        value: {},
      },
    });
  };

  const onBrandFilterPlacementChange = placement => {
    handleChange({
      target: {
        name: 'watermark_config',
        value: {
          ...values.watermark_config,
          placement,
        },
      },
    });
  };

  return (
    <Fragment>
      <div className="brand-filter-wrap">
        <ImageUpload
          disabled={disabled}
          id="brand-filter-upload"
          className="mb-3"
          loading={loading}
          image={
            values.watermark_config.asset_image?.id
              ? {
                  ...values.watermark_config.asset_image,
                  thumbnail_urls: values.watermark_config.asset_image.url
                    ? [
                        {
                          size_code: IMAGE_SIZE_CODE.LARGE2X,
                          url: values.watermark_config.asset_image.url,
                          width: 1,
                          height: 1,
                        },
                      ]
                    : [],
                }
              : null
          }
          onInputChange={onBrandFilterChange}
          onImageDismiss={onBrandFilterDismiss}
          buttonText={`Upload your ${
            [BASENAME.SHUTTERBUG, undefined].includes(basename)
              ? 'custom photo overlay'
              : 'brand filter'
          }`}
        />

        {(values.watermark_config?.id ||
          values.watermark_config?.asset_image?.id) &&
          !disabled && (
            <div className="row">
              <div className="col-md-6 col-12">
                <a
                  href="#"
                  className="brand-filter-card brand-filter-position-setting"
                  onClick={event => {
                    event.preventDefault();

                    if (
                      values.watermark_config?.id ||
                      values.watermark_config?.asset_image?.id
                    ) {
                      onBrandFilterPlacementChange(WATERMARK_PLACEMENTS.SE);
                    }
                  }}
                >
                  <CustomControl
                    id="brand-filter-corner"
                    type="checkbox"
                    label="Bottom right corner"
                    className="brand-filter-control"
                    size="sm"
                    disabled={
                      !values.watermark_config?.id &&
                      !values.watermark_config?.asset_image?.id
                    }
                    checked={
                      values.watermark_config?.placement ===
                      WATERMARK_PLACEMENTS.SE
                    }
                    readOnly
                  />

                  <figure className="brand-filter-example">
                    <img
                      src={getAsset('/brand-filter-example-corner.jpg')}
                      alt=""
                    />
                  </figure>

                  <div className="brand-filter-note">
                    Suggested file: 700px min width, PNG
                  </div>
                </a>
              </div>

              <div className="col-md-6 col-12">
                <a
                  href="#"
                  className="brand-filter-card brand-filter-position-setting"
                  onClick={event => {
                    event.preventDefault();

                    if (
                      values.watermark_config?.id ||
                      values.watermark_config?.asset_image?.id
                    ) {
                      onBrandFilterPlacementChange(WATERMARK_PLACEMENTS.SC);
                    }
                  }}
                >
                  <CustomControl
                    id="brand-filter-fullwidth"
                    type="checkbox"
                    label="Full bottom banner"
                    className="brand-filter-control"
                    size="sm"
                    disabled={
                      !values.watermark_config?.id &&
                      !values.watermark_config?.asset_image?.id
                    }
                    checked={
                      values.watermark_config?.placement ===
                      WATERMARK_PLACEMENTS.SC
                    }
                    readOnly
                  />

                  <figure className="brand-filter-example">
                    <img
                      src={getAsset('/brand-filter-example-ribbon.jpg')}
                      alt=""
                    />
                  </figure>

                  <div className="brand-filter-note">
                    Suggested file: 2560x120, PNG
                  </div>
                </a>
              </div>
            </div>
          )}
      </div>

      {error && <Alert type="danger">{error}</Alert>}
    </Fragment>
  );
};

AlbumFolderBranding.propTypes = propTypes;

const withUserData = graphql(MeQuery, {
  props: ({ data } = {}) => ({
    userId: data?.me?.id,
  }),
});

const withAssetImageUpload = graphql(assetImagesUploadMutation, {
  props: ({ mutate }) => ({
    assetImagesUpload: (accountId, images) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            images,
          },
        },
      }),
  }),
});

const mapDispatchToProps = {
  photoUploadSuccess: photoUploadSuccessAction,
};

export default compose(
  withUserData,
  withAssetImageUpload,
  connect(null, mapDispatchToProps),
)(AlbumFolderBranding);
